import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CaseLawHeader from '../../components/caselaws/CaseLawHeader';
import { INITIAL_CASE_LAWS } from '../../data/caseLawsData';
import config from '../../config';
import {
  Search,
  BookOpen,
  Calendar,
  Layers,
  Scale,
  Sparkles,
  ArrowRight,
  FileText,
  Cpu,
  Activity,
  ChevronLeft,
  ChevronRight,
  RefreshCw
} from 'lucide-react';

const API_BASE = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:5000'
  : '';

const CaseLawsHub = ({ defaultCategory = 'ALL' }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Detect category from URL route (e.g. /case-laws/gst or /case-laws/income-tax)
  const initialCategory = useMemo(() => {
    if (location.pathname.includes('/gst')) return 'GST';
    if (location.pathname.includes('/income-tax')) return 'INCOME_TAX';
    return defaultCategory;
  }, [location.pathname, defaultCategory]);

  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFavor, setSelectedFavor] = useState('ALL');
  const [sortBy, setSortBy] = useState('newest');

  const [cases, setCases] = useState(INITIAL_CASE_LAWS);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCases, setTotalCases] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [crawlerStatus, setCrawlerStatus] = useState({
    isRunning: true,
    totalSaved: 0,
    dbCount: 1400,
    incomeTax: 700,
    gst: 700,
    status: 'Ingesting all case laws continuously 24/7'
  });

  // Status telemetry fetcher
  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/caselaws/crawler/status`);
      const json = await res.json();
      if (json && json.success && json.data) {
        setCrawlerStatus(json.data);
      }
    } catch (err) {
      // silent fallback
    }
  };

  // Poll live telemetry every 30s so indexed counts increment, and gently sync case laws hourly
  useEffect(() => {
    fetchStatus();

    // Live counter telemetry check every 30 seconds
    const statusInterval = setInterval(fetchStatus, 30000);

    // Hourly background sync (every 60 minutes) to update case listings
    const ONE_HOUR = 60 * 60 * 1000;
    const caseListInterval = setInterval(() => {
      fetchStatus();
      setRefreshKey(k => k + 1);
    }, ONE_HOUR);

    return () => {
      clearInterval(statusInterval);
      clearInterval(caseListInterval);
    };
  }, []);

  const handleManualSync = () => {
    fetchStatus();
    setRefreshKey(k => k + 1);
  };


  // Dynamic Google SEO tags
  useEffect(() => {
    const pageTitle = activeCategory === 'GST'
      ? 'GST Case Laws & Advance Rulings [AAR / HC / SC] | Tax Eases Legal AI'
      : activeCategory === 'INCOME_TAX'
      ? 'Income Tax & ITAT Case Laws [Appeals / Reassessment / High Court] | Tax Eases Legal AI'
      : 'Indian Tax Case Laws Hub - GST & Income Tax Judgments & AI Summaries | Tax Eases';

    document.title = pageTitle;

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Search and read landmark Indian GST and Income Tax judgments with 50/50 split-screen verbatim text and automated AI executive summaries. Updated continuously from ITAT, SC, HC, and AAR.'
      );
    }
  }, [activeCategory]);

  // Sync category state when route changes
  useEffect(() => {
    if (location.pathname.includes('/gst')) {
      setActiveCategory('GST');
    } else if (location.pathname.includes('/income-tax')) {
      setActiveCategory('INCOME_TAX');
    } else {
      setActiveCategory('ALL');
    }
    setPage(1);
  }, [location.pathname]);

  // Fetch Cases from API with instant fallback to INITIAL_CASE_LAWS
  useEffect(() => {
    let isCancelled = false;

    const fetchCases = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: '24',
          sort: sortBy
        });

        if (activeCategory !== 'ALL') {
          params.append('category', activeCategory);
        }
        if (searchQuery.trim()) {
          params.append('q', searchQuery.trim());
        }
        if (selectedFavor !== 'ALL') {
          params.append('favor', selectedFavor);
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 7000);

        const res = await fetch(`${API_BASE}/api/caselaws?${params.toString()}`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        const data = await res.json();
        if (!isCancelled && data.success && Array.isArray(data.data) && data.data.length > 0) {
          setCases(data.data);
          if (data.pagination) {
            setTotalPages(data.pagination.totalPages || 1);
            setTotalCases(data.pagination.total || data.data.length);
          }
          setLoading(false);
          return;
        }
      } catch (err) {
        // Fall back cleanly
      }

      // Filter local fallback dataset client-side
      if (!isCancelled) {
        let filtered = [...INITIAL_CASE_LAWS];

        if (activeCategory !== 'ALL') {
          filtered = filtered.filter(c => c.category === activeCategory);
        }

        if (selectedFavor !== 'ALL') {
          filtered = filtered.filter(c => c.favor === selectedFavor);
        }

        if (searchQuery.trim()) {
          const q = searchQuery.trim().toLowerCase();
          filtered = filtered.filter(c =>
            c.caseId.toLowerCase().includes(q) ||
            c.caseName.toLowerCase().includes(q) ||
            c.court.toLowerCase().includes(q) ||
            c.sections?.some(s => s.toLowerCase().includes(q)) ||
            c.summary?.quickTakeaway?.toLowerCase().includes(q)
          );
        }

        setCases(filtered);
        setTotalPages(1);
        setTotalCases(filtered.length);
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchCases, 120);
    return () => {
      isCancelled = true;
      clearTimeout(debounce);
    };
  }, [activeCategory, searchQuery, selectedFavor, sortBy, page, refreshKey]);

  const handleCategorySwitch = (cat) => {
    setActiveCategory(cat);
    setPage(1);
    if (cat === 'GST') navigate('/case-laws/gst');
    else if (cat === 'INCOME_TAX') navigate('/case-laws/income-tax');
    else navigate('/case-laws');
  };


  const getFavorBadge = (favor) => {
    switch (favor) {
      case 'Assessee':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-black bg-emerald-500/25 text-emerald-300 border border-emerald-400/50 shadow-sm shadow-emerald-500/20">
            ✓ In Favour: Assessee
          </span>
        );
      case 'Revenue':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-black bg-amber-500/25 text-amber-300 border border-amber-400/50 shadow-sm shadow-amber-500/20">
            In Favour: Revenue
          </span>
        );
      case 'Remanded':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-black bg-purple-500/25 text-purple-300 border border-purple-400/50">
            Remanded Back
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-slate-700/50 text-slate-200 border border-slate-600">
            Clarification
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans flex flex-col relative overflow-hidden">
      {/* Liquid Mesh Animated Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(15,23,42,1),rgba(2,6,23,1))]"></div>
        <div className="absolute top-[-10%] left-[-10%] w-[55%] h-[55%] bg-purple-700 rounded-full mix-blend-screen filter blur-[140px] opacity-25 animate-blob"></div>
        <div className="absolute top-[-10%] right-[-10%] w-[55%] h-[55%] bg-cyan-600 rounded-full mix-blend-screen filter blur-[140px] opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-[-10%] left-[25%] w-[55%] h-[55%] bg-blue-700 rounded-full mix-blend-screen filter blur-[140px] opacity-25 animate-blob animation-delay-4000"></div>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}></div>
      </div>

      {/* Dedicated Legal AI Header */}
      <CaseLawHeader />

      {/* Hero Section */}
      <section className="relative z-10 pt-10 pb-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full text-center">
        {/* Glowing Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/15 border border-cyan-400/40 text-cyan-300 text-xs font-bold mb-4 backdrop-blur-md shadow-lg shadow-cyan-500/20">
          <Sparkles className="w-3.5 h-3.5 text-cyan-400 animate-pulse" />
          Autonomous Legal AI Intelligence Engine
        </div>

        {/* HIGH CONTRAST LUMINOUS HEADLINE */}
        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-4 drop-shadow-[0_2px_15px_rgba(0,0,0,0.8)]">
          {activeCategory === 'INCOME_TAX' ? (
            <>
              Income Tax & ITAT{' '}
              <span className="bg-gradient-to-r from-cyan-300 via-teal-200 to-white text-transparent bg-clip-text drop-shadow-[0_2px_14px_rgba(34,211,238,0.6)]">
                Case Laws
              </span>
            </>
          ) : activeCategory === 'GST' ? (
            <>
              GST & AAR{' '}
              <span className="bg-gradient-to-r from-emerald-300 via-teal-200 to-white text-transparent bg-clip-text drop-shadow-[0_2px_14px_rgba(52,211,153,0.6)]">
                Legal Rulings
              </span>
            </>
          ) : (
            <>
              Tax Eases{' '}
              <span className="bg-gradient-to-r from-cyan-300 via-teal-200 to-white text-transparent bg-clip-text drop-shadow-[0_2px_14px_rgba(34,211,238,0.6)]">
                Case Law Hub
              </span>
            </>
          )}
        </h1>

        {/* HIGH CONTRAST SUBTITLE */}
        <p className="text-sm sm:text-base text-slate-100 max-w-3xl mx-auto mb-8 font-normal leading-relaxed drop-shadow-sm">
          Daily judgments collected continuously from ITAT, Supreme Court, High Courts, and GST Tribunals.
          Divided into a seamless 50/50 split-screen view: full court text alongside automated AI summaries.
        </p>

        {/* Liquid Glass Search Bar */}
        <div className="max-w-3xl mx-auto bg-slate-900/90 backdrop-blur-2xl p-2.5 rounded-2xl border border-cyan-400/40 shadow-2xl shadow-cyan-950/70 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-3.5 w-5 h-5 text-cyan-400" />
            <input
              type="text"
              placeholder="Search by Case ID, Party Name, Section (e.g. 148, 68, 16(4)), or Tribunal..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="w-full pl-12 pr-4 py-3 bg-slate-950 text-white rounded-xl placeholder-slate-400 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-cyan-400 border border-slate-700 shadow-inner"
            />
          </div>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="px-4 py-2 text-xs font-bold text-slate-300 hover:text-white bg-white/10 rounded-xl self-center"
            >
              Clear
            </button>
          )}
        </div>

        {/* Dynamic Stats Grid - Full Width & Clean Breathing Room */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-5xl mx-auto mt-8">
          <div className="bg-slate-900/80 border border-slate-700/80 rounded-2xl p-4 backdrop-blur-md shadow-lg shadow-slate-950/50 text-center">
            <div className="text-2xl sm:text-3xl font-black text-white tracking-tight">
              {(crawlerStatus?.dbCount || cases.length).toLocaleString()}+
            </div>
            <div className="text-xs text-slate-300 font-semibold mt-1 flex items-center justify-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5 text-cyan-400" />
              Indexed Cases
            </div>
          </div>
          <div className="bg-slate-900/80 border border-slate-700/80 rounded-2xl p-4 backdrop-blur-md shadow-lg shadow-slate-950/50 text-center">
            <div className="text-2xl sm:text-3xl font-black text-cyan-300 tracking-tight">
              {(crawlerStatus?.incomeTax ?? cases.filter(c => c.category === 'INCOME_TAX').length).toLocaleString()}
            </div>
            <div className="text-xs text-slate-300 font-semibold mt-1 flex items-center justify-center gap-1.5">
              <Scale className="w-3.5 h-3.5 text-cyan-400" />
              Income Tax
            </div>
          </div>
          <div className="bg-slate-900/80 border border-slate-700/80 rounded-2xl p-4 backdrop-blur-md shadow-lg shadow-slate-950/50 text-center">
            <div className="text-2xl sm:text-3xl font-black text-teal-300 tracking-tight">
              {(crawlerStatus?.gst ?? cases.filter(c => c.category === 'GST').length).toLocaleString()}
            </div>
            <div className="text-xs text-slate-300 font-semibold mt-1 flex items-center justify-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-teal-400" />
              GST Orders
            </div>
          </div>
          <div className="bg-slate-900/80 border border-slate-700/80 rounded-2xl p-4 backdrop-blur-md shadow-lg shadow-slate-950/50 text-center">
            <div className="text-2xl sm:text-3xl font-black text-emerald-300 tracking-tight">100%</div>
            <div className="text-xs text-slate-300 font-semibold mt-1 flex items-center justify-center gap-1.5">
              <Cpu className="w-3.5 h-3.5 text-emerald-400" />
              AI Analyzed
            </div>
          </div>
        </div>

        {/* Autonomous Agent Status Badge - Next Line */}
        <div className="flex justify-center mt-4">
          <div className="inline-flex flex-wrap items-center justify-center gap-2.5 px-5 py-2.5 rounded-2xl bg-cyan-950/80 border border-cyan-400/40 text-cyan-300 text-xs font-bold shadow-lg shadow-cyan-950/60 backdrop-blur-md">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block shadow-sm shadow-emerald-400/50 animate-pulse" />
            <span className="tracking-wide">Autonomous Agent: Active in Background</span>
            <span className="text-slate-300 font-medium hidden sm:inline border-l border-cyan-500/30 pl-2.5">
              Continuously indexing cases • Fresh updates sync hourly
            </span>
            <button
              onClick={handleManualSync}
              title="Sync latest judgments immediately"
              className="ml-1 inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-200 border border-cyan-400/30 text-xs font-bold transition-all hover:scale-105 cursor-pointer shadow-sm shadow-cyan-500/10"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Sync Now
            </button>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1 w-full">
        {/* Category Navigation Pills */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-800 pb-6 mb-8">
          <div className="flex items-center gap-2 bg-slate-900/90 p-1.5 rounded-2xl border border-slate-700 backdrop-blur-md">
            <button
              onClick={() => handleCategorySwitch('ALL')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                activeCategory === 'ALL'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-slate-950 font-black shadow-lg shadow-cyan-500/30'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              All Judgments
            </button>
            <button
              onClick={() => handleCategorySwitch('INCOME_TAX')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                activeCategory === 'INCOME_TAX'
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-slate-950 font-black shadow-lg shadow-blue-500/30'
                  : 'text-slate-300 hover:text-cyan-300'
              }`}
            >
              <Scale className="w-3.5 h-3.5" />
              Income Tax
            </button>
            <button
              onClick={() => handleCategorySwitch('GST')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                activeCategory === 'GST'
                  ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-slate-950 font-black shadow-lg shadow-teal-500/30'
                  : 'text-slate-300 hover:text-teal-300'
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              GST Rulings
            </button>
          </div>

          {/* Filter by outcome */}
          <div className="flex items-center gap-3">
            <select
              value={selectedFavor}
              onChange={(e) => setSelectedFavor(e.target.value)}
              className="bg-slate-900 border border-slate-700 text-slate-100 text-xs font-bold rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-400"
            >
              <option value="ALL">All Outcomes</option>
              <option value="Assessee">In Favour of Assessee</option>
              <option value="Revenue">In Favour of Revenue</option>
            </select>
          </div>
        </div>

        {/* Loading Spinner */}
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center text-center">
            <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-slate-200 font-bold text-sm">Searching judicial intelligence...</p>
          </div>
        ) : cases.length === 0 ? (
          <div className="bg-slate-900/80 rounded-3xl border border-slate-800 p-12 text-center backdrop-blur-xl shadow-2xl">
            <BookOpen className="w-12 h-12 text-slate-500 mx-auto mb-3" />
            <h3 className="text-xl font-black text-white">No Case Laws Found</h3>
            <p className="text-sm text-slate-300 max-w-md mx-auto mt-2 mb-6">
              No matching judgment for "{searchQuery}". Try searching by Section number (e.g. 148, 68, 16(4)), or court name.
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedFavor('ALL');
              }}
              className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-slate-950 text-xs font-black rounded-xl hover:from-cyan-400 hover:to-blue-500 transition shadow-lg shadow-cyan-500/20"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          /* Cards Grid with Maximum Text Legibility */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cases.map((item) => (
              <div
                key={item._id || item.slug}
                className="group relative bg-slate-900/90 hover:bg-slate-900 border border-slate-700/80 hover:border-cyan-400 rounded-3xl p-6 backdrop-blur-xl transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/20 flex flex-col justify-between"
              >
                <div>
                  {/* Top Bar: Category & Outcome */}
                  <div className="flex items-center justify-between gap-2 mb-3.5">
                    <span className={`text-xs font-black uppercase tracking-wider px-3 py-1 rounded-lg ${
                      item.category === 'GST'
                        ? 'bg-teal-500/20 text-teal-300 border border-teal-400/50'
                        : 'bg-blue-500/20 text-blue-300 border border-blue-400/50'
                    }`}>
                      {item.category === 'GST' ? 'GST Ruling' : 'Income Tax'}
                    </span>
                    {getFavorBadge(item.favor)}
                  </div>

                  {/* Case ID / Citation */}
                  <div className="text-sm font-black text-cyan-300 mb-1.5 flex items-center gap-1.5">
                    <FileText className="w-4 h-4 text-cyan-400" />
                    {item.caseId}
                  </div>

                  {/* Case Title - Extra Clear & Bold */}
                  <h3 className="text-lg font-black text-white mb-2 leading-snug group-hover:text-cyan-300 transition-colors">
                    <Link to={`/case-laws/${item.slug}`}>
                      {item.caseName}
                    </Link>
                  </h3>

                  {/* Court & Date */}
                  <div className="flex items-center gap-2 text-xs text-slate-300 mb-4 font-semibold">
                    <span className="truncate">{item.court}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1 whitespace-nowrap text-slate-300">
                      <Calendar className="w-3.5 h-3.5 text-cyan-400" />
                      {new Date(item.orderDate).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                  </div>

                  {/* Sections */}
                  {item.sections && item.sections.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {item.sections.slice(0, 3).map((sec, idx) => (
                        <span
                          key={idx}
                          className="bg-cyan-950/80 border border-cyan-400/50 text-cyan-200 text-xs font-bold px-2.5 py-1 rounded-lg shadow-sm"
                        >
                          {sec}
                        </span>
                      ))}
                      {item.sections.length > 3 && (
                        <span className="text-xs text-slate-300 font-bold self-center">
                          +{item.sections.length - 3} more
                        </span>
                      )}
                    </div>
                  )}

                  {/* AI Quick Takeaway - Maximum Readability Box */}
                  {item.summary?.quickTakeaway && (
                    <div className="bg-slate-950/95 border border-cyan-500/40 rounded-2xl p-4 text-xs sm:text-sm text-slate-100 font-normal leading-relaxed mb-4 shadow-inner">
                      <span className="font-extrabold text-cyan-300 text-xs uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                        AI Executive Summary:
                      </span>
                      {item.summary.quickTakeaway}
                    </div>
                  )}
                </div>

                {/* Card CTA */}
                <div className="pt-4 border-t border-slate-800 flex items-center justify-between">
                  <span className="text-xs text-cyan-400 font-bold">
                    Split-Screen View
                  </span>
                  <Link
                    to={`/case-laws/${item.slug}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/50 text-xs font-black text-cyan-300 group-hover:translate-x-0.5 transition-all shadow-md"
                  >
                    Read & Summarize
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Dynamic Multi-Page Pagination Controls */}
        {!loading && totalPages > 1 && (
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-900/90 border border-slate-700/80 rounded-2xl p-4 backdrop-blur-xl shadow-xl">
            <div className="text-xs text-slate-300 font-medium">
              Page <span className="font-black text-cyan-300">{page}</span> of <span className="font-black text-white">{totalPages}</span>
              {totalCases > 0 && (
                <span className="ml-2 text-slate-400 font-normal">
                  ({totalCases.toLocaleString('en-IN')} cases indexed)
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (page > 1) {
                    setPage(p => p - 1);
                    window.scrollTo({ top: 380, behavior: 'smooth' });
                  }
                }}
                disabled={page <= 1}
                className="inline-flex items-center gap-1 px-3.5 py-2 rounded-xl text-xs font-bold transition-all bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed border border-slate-700"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              
              {/* Numeric Page Buttons */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pNum;
                  if (totalPages <= 5) {
                    pNum = i + 1;
                  } else if (page <= 3) {
                    pNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pNum = totalPages - 4 + i;
                  } else {
                    pNum = page - 2 + i;
                  }
                  return (
                    <button
                      key={pNum}
                      onClick={() => {
                        setPage(pNum);
                        window.scrollTo({ top: 380, behavior: 'smooth' });
                      }}
                      className={`w-9 h-9 rounded-xl text-xs font-black transition-all ${
                        page === pNum
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-slate-950 shadow-md shadow-cyan-500/30 font-black'
                          : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700 hover:text-white border border-slate-700/60'
                      }`}
                    >
                      {pNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => {
                  if (page < totalPages) {
                    setPage(p => p + 1);
                    window.scrollTo({ top: 380, behavior: 'smooth' });
                  }
                }}
                disabled={page >= totalPages}
                className="inline-flex items-center gap-1 px-3.5 py-2 rounded-xl text-xs font-bold transition-all bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed border border-slate-700"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </main>



      {/* Dark Liquid Footer */}
      <footer className="relative z-10 border-t border-slate-800 bg-black/60 backdrop-blur-md py-6 text-center text-xs text-slate-400">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Scale className="w-4 h-4 text-cyan-400" />
            <span className="text-white font-black">Tax Eases Legal AI</span>
            <span>• Case Laws & Automated Executive Summaries</span>
          </div>
          <div className="text-slate-400 font-medium">
            Google Search Engine Indexed • Continuous Crawler Active
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CaseLawsHub;
